import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { FadeIn } from '../components/FadeIn';
const sections = [{
  number: '01',
  heading: 'Introduction',
  paragraphs: ['Cognarchism respects the privacy of visitors, readers, subscribers, and users of this website. This Privacy Policy describes what information may be collected through the site, how that information may be used, and the choices available to users regarding their information.', 'By accessing or using this website, you acknowledge that you have read and understood this Privacy Policy.']
}, {
  number: '02',
  heading: 'Information We Collect',
  paragraphs: ['We may collect information in the following categories:'],
  definitions: [{
    title: 'Information You Provide Directly',
    desc: 'This may include your name, email address, organization, or other information you submit when contacting us, subscribing to updates, submitting inquiries, or completing forms on the site.'
  }, {
    title: 'Automatically Collected Information',
    desc: 'We may collect technical information automatically when you visit the site, including IP address, browser type, device type, operating system, referral source, pages viewed, session duration, and general usage behavior.'
  }, {
    title: 'Cookies and Similar Technologies',
    desc: 'We may use cookies, analytics tools, and similar technologies to improve site performance, understand visitor behavior, remember preferences, and enhance the user experience.'
  }]
}, {
  number: '03',
  heading: 'How We Use Information',
  paragraphs: ['We may use collected information to:'],
  list: ['Operate and maintain the website', 'Respond to inquiries and communications', 'Send newsletters, updates, or requested materials', 'Improve site content, design, and performance', 'Understand user engagement and traffic patterns', 'Protect the site against misuse, fraud, or unauthorized activity', 'Comply with legal obligations where required'],
  afterList: 'We do not collect personal information for purposes unrelated to the operation, communication, development, or security of the site.'
}, {
  number: '04',
  heading: 'Sharing of Information',
  paragraphs: ['We do not sell personal information to third parties.', 'We may share information in limited circumstances, including:'],
  list: ['With service providers who help operate the site or communications systems', 'Where disclosure is required by law, regulation, or legal process', 'Where necessary to protect rights, safety, security, or the integrity of the website', 'In connection with a restructuring, merger, transfer, or similar organizational change, if applicable'],
  afterList: 'Any third-party service providers used for analytics, email delivery, hosting, or form processing may process information on our behalf subject to their own contractual or legal obligations.'
}, {
  number: '05',
  heading: 'Cookies and Analytics',
  paragraphs: ['This site may use cookies and analytics tools to understand how visitors use the website and to improve performance and usability. Cookies are small files stored on your device that help websites remember preferences and recognize repeat visits.', 'You may be able to control cookies through your browser settings. Disabling cookies may affect certain site functions.', 'If analytics services are used, they may collect information such as pages visited, time on site, device information, and approximate geographic region.']
}, {
  number: '06',
  heading: 'Data Retention',
  paragraphs: ['We retain personal information only for as long as reasonably necessary for the purposes described in this Privacy Policy, including communication, site administration, legal compliance, recordkeeping, and security.', 'If you submit contact information or subscribe to communications, we may retain those records until they are no longer needed or until you request removal where applicable.']
}, {
  number: '07',
  heading: 'Data Security',
  paragraphs: ['We take reasonable administrative, technical, and organizational measures to protect information against unauthorized access, misuse, loss, disclosure, or alteration. However, no internet transmission or storage system can be guaranteed to be completely secure.', 'Users submit information at their own risk, and while reasonable safeguards may be used, absolute security cannot be guaranteed.']
}, {
  number: '08',
  heading: 'Your Choices',
  paragraphs: ['Depending on your jurisdiction and the nature of your interaction with the site, you may have the ability to:'],
  list: ['Request access to personal information we hold about you', 'Request correction of inaccurate information', 'Request deletion of information where appropriate', 'Unsubscribe from email communications', 'Adjust cookie settings through your browser'],
  afterList: 'Requests may be submitted through the contact information provided on this site.'
}, {
  number: '09',
  heading: 'Third-Party Links',
  paragraphs: ['This website may contain links to third-party websites, platforms, or publications. We are not responsible for the privacy practices, content, or policies of those third parties. Users should review the privacy policies of any external sites they visit.']
}, {
  number: '10',
  heading: "Children's Privacy",
  paragraphs: ['This website is not intended for children under the age of 13, or under a higher minimum age where required by local law. We do not knowingly collect personal information from children without lawful basis or appropriate consent.', 'If you believe that a child has submitted personal information through the site, please contact us so appropriate action can be taken.']
}, {
  number: '11',
  heading: 'International Visitors',
  paragraphs: ['If you access this website from outside the jurisdiction in which it is operated, your information may be transferred to and processed in other jurisdictions where privacy laws may differ. By using the site, you acknowledge and accept such transfer and processing where lawful.']
}, {
  number: '12',
  heading: 'Changes to This Policy',
  paragraphs: ['We may update this Privacy Policy from time to time to reflect operational, legal, or organizational changes. Any revised version will be posted on this page with an updated effective date.', 'Continued use of the website after changes are posted constitutes acceptance of the revised Privacy Policy.']
}, {
  number: '13',
  heading: 'Contact',
  paragraphs: ['For questions about this Privacy Policy or requests regarding your information, contact:'],
  contactBlock: true
}];
export function PrivacyPolicy() {
  return <article data-id="element-402">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-cognarch-white border-b border-cognarch-border" data-id="element-403">
        <div className="max-w-3xl mx-auto px-6" data-id="element-404">
          <FadeIn delay={0.1} direction="down" data-id="element-405">
            <p className="uppercase tracking-[0.25em] text-xs font-medium text-cognarch-gray mb-8" data-id="element-406">
              Legal
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-407">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-medium leading-[1.1] tracking-tight text-cognarch-black mb-8" data-id="element-408">
              Privacy Policy
            </h1>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-409">
            <p className="text-lg md:text-xl text-cognarch-gray leading-relaxed mb-6 max-w-2xl" data-id="element-410">
              This Privacy Policy explains how Cognarchism collects, uses,
              stores, and protects information when you visit this website,
              submit forms, or interact with our content and services.
            </p>
            <p className="text-sm text-cognarch-gray/70" data-id="element-411">
              Effective Date: March 28, 2026
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Body Sections */}
      {sections.map((section, index) => <section key={section.number} className={`py-20 md:py-28 border-b border-cognarch-border ${index % 2 === 1 ? 'bg-cognarch-offwhite' : 'bg-cognarch-white'}`} data-id="element-412">
          <div className="max-w-3xl mx-auto px-6" data-id="element-413">
            <FadeIn data-id="element-414">
              <div className="flex items-baseline gap-6 mb-8" data-id="element-415">
                <span className="text-cognarch-border font-serif text-4xl md:text-5xl leading-none select-none" data-id="element-416">
                  {section.number}
                </span>
                <h2 className="text-2xl md:text-4xl font-serif text-cognarch-charcoal leading-tight" data-id="element-417">
                  {section.heading}
                </h2>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-418">
              <div className="space-y-6" data-id="element-419">
                {section.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-420">
                    {paragraph}
                  </p>)}

                {section.definitions && <div className="space-y-8 py-4 border-t border-cognarch-border mt-8 pt-8" data-id="element-421">
                    {section.definitions.map((def, dIndex) => <div key={dIndex} className="flex gap-4" data-id="element-422">
                        <span className="text-cognarch-charcoal/30 text-xs mt-1.5 shrink-0" data-id="element-423">
                          ◆
                        </span>
                        <div data-id="element-424">
                          <h3 className="text-cognarch-charcoal font-serif text-lg md:text-xl mb-2" data-id="element-425">
                            {def.title}
                          </h3>
                          <p className="text-cognarch-gray leading-[1.8] text-base" data-id="element-426">
                            {def.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>}

                {section.list && <ul className="space-y-3 py-4 pl-1" data-id="element-427">
                    {section.list.map((item, lIndex) => <li key={lIndex} className="flex items-start gap-3 text-cognarch-gray text-base" data-id="element-428">
                        <span className="text-cognarch-charcoal/30 text-xs mt-1.5" data-id="element-429">
                          ◆
                        </span>
                        {item}
                      </li>)}
                  </ul>}

                {section.afterList && <p className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-430">
                    {section.afterList}
                  </p>}

                {section.contactBlock && <div className="border-t border-cognarch-border mt-8 pt-8 space-y-2" data-id="element-431">
                    <p className="text-cognarch-charcoal font-serif text-lg" data-id="element-432">
                      Cognarchism
                    </p>
                    <p className="text-cognarch-gray text-base" data-id="element-433">
                      [Insert Contact Email]
                    </p>
                    <p className="text-cognarch-gray text-base" data-id="element-434">
                      [Insert Mailing Address or Business Address if applicable]
                    </p>
                  </div>}
              </div>
            </FadeIn>
          </div>
        </section>)}

      {/* Closing Statement Block */}
      <section className="py-24 md:py-32 bg-cognarch-black text-white" data-id="element-435">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-436">
          <FadeIn data-id="element-437">
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-438" />
            <p className="text-xl md:text-2xl font-serif leading-relaxed text-white/80 mb-12 text-balance" data-id="element-439">
              Cognarchism is committed to handling information responsibly,
              transparently, and with respect for user privacy.
            </p>
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-440" />
            <p className="uppercase tracking-[0.25em] text-sm text-white/50 font-medium" data-id="element-441">
              Clear principles. Transparent use. Responsible stewardship.
            </p>
          </FadeIn>
        </div>
      </section>
    </article>;
}